import { useEffect, useRef } from 'react';

/**
 * useVersionCheck
 *
 * Detecta automaticamente cuando hay una nueva version del frontend desplegada
 * y recarga la pagina para que el usuario vea los cambios sin borrar cache manualmente.
 *
 * Como funciona:
 *   - Create React App genera en cada build un `asset-manifest.json` cuyo contenido
 *     (los nombres de los bundles con hash) cambia siempre que cambia el codigo.
 *   - Al arrancar guardamos una "huella" de ese manifest como linea base.
 *   - Cada cierto tiempo (y al volver a la pestana) volvemos a pedir el manifest
 *     SIN cache. Si la huella cambio, significa que hay un deploy nuevo -> recargamos.
 *
 * No necesita cambios en el proceso de build: aprovecha un archivo que CRA ya crea.
 *
 * @param {object}  [opts]
 * @param {number}  [opts.intervalMs=60000] Cada cuanto revisar (ms). Default 60s.
 * @param {boolean} [opts.enabled=true]     Permite desactivarlo (p. ej. en desarrollo).
 */
export default function useVersionCheck({ intervalMs = 60000, enabled = true } = {}) {
  // Huella de la version cargada actualmente. Se fija en el primer chequeo exitoso.
  const baselineRef = useRef(null);
  // Evita disparar mas de una recarga si varios chequeos coinciden.
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    // Construye una huella estable a partir del manifest.
    const fingerprintFrom = (manifest) => {
      if (!manifest || typeof manifest !== 'object') return null;
      // `files` mapea nombre-logico -> ruta-con-hash. Su serializacion cambia
      // en cada build. Si no existe, caemos a serializar el objeto completo.
      const source = manifest.files || manifest;
      try {
        return JSON.stringify(source);
      } catch {
        return null;
      }
    };

    const check = async () => {
      if (reloadingRef.current) return;
      try {
        // `cache: 'no-store'` garantiza que siempre pedimos la version real del
        // servidor y no una copia en cache del navegador.
        const res = await fetch(`${process.env.PUBLIC_URL || ''}/asset-manifest.json`, {
          cache: 'no-store',
        });
        if (!res.ok) return; // p. ej. servidor de desarrollo sin manifest
        const manifest = await res.json();
        if (cancelled) return;

        const fingerprint = fingerprintFrom(manifest);
        if (!fingerprint) return;

        if (baselineRef.current === null) {
          // Primer chequeo: fijamos la linea base (la version que el usuario tiene abierta).
          baselineRef.current = fingerprint;
          return;
        }

        if (fingerprint !== baselineRef.current) {
          // Hay un deploy nuevo -> recargar. No hace falta limpiar la base porque
          // tras el reload la app arranca de cero y fija su nueva linea base.
          reloadingRef.current = true;
          window.location.reload();
        }
      } catch {
        // Errores de red o parseo: ignorar y reintentar en el proximo intervalo.
      }
    };

    // Chequeo inicial para fijar la linea base cuanto antes.
    check();

    const intervalId = setInterval(check, intervalMs);

    // Revisar tambien cuando el usuario vuelve a la pestana: es el momento mas
    // probable de que exista un deploy nuevo desde que se fue.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled]);
}
